import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/motion";
import { SERVICE_LIST } from "../lib/services";
import { Seo } from "../components/Seo";

export default function Services() {
  return (
    <>
      <Seo
        title="Construction Services | Next Horizon Builders"
        description="Explore preconstruction, ground-up multifamily construction, project management, and design-build coordination services from Next Horizon Builders."
        path="/services"
        image="/media/generated/services-hero.jpg"
      />
      <PageHero
        eyebrow="Our Services"
        titleLines={["Services"]}
        subtitle="End-to-end construction services built on experience, integrity, and a commitment to delivering exceptional results."
        image="/media/generated/services-hero.jpg"
        imageAlt="Next Horizon Builders worker overlooking a Southern California construction site"
      />

      {/* Intro + service rows */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Our Approach"
            title="Construction Solutions Tailored to Every Project"
            subtitle="Every project has unique goals, timelines, and delivery requirements. Our team works alongside owners, agencies, and project stakeholders to provide the right construction approach, ensuring efficient coordination, quality execution, and dependable results from concept through completion."
          />

          <div className="mt-20 space-y-24 md:space-y-32">
            {SERVICE_LIST.map((s, i) => (
              <Reveal key={s.slug}>
                <div className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <Link to={`/services/${s.slug}`} className="group relative overflow-hidden aspect-[4/3] block">
                    <img
                      src={s.listImage || s.image}
                      alt={s.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-navy/10 group-hover:bg-navy/0 transition-colors duration-500" />
                    <span className="absolute top-6 left-6 font-serif text-white text-2xl drop-shadow">{String(i + 1).padStart(2, "0")}</span>
                  </Link>
                  <div>
                    <p className="eyebrow mb-4">Service {String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif font-light text-4xl md:text-5xl text-navy tracking-tight">{s.name}</h3>
                    <p className="mt-6 text-darkgray/70 text-lg leading-relaxed font-light">{s.short}</p>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`service-link-${s.slug}`}
                      className="group mt-8 inline-flex items-center gap-3 text-navy text-xs font-semibold uppercase tracking-[0.2em]"
                    >
                      Explore Service
                      <ArrowUpRight size={18} className="text-gold group-hover:rotate-45 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy grain py-24 md:py-32 relative overflow-hidden">
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <SectionHeading
            light
            eyebrow="Start Here"
            title={<>Let's build your<br />next project together.</>}
          />
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 bg-gold text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300 whitespace-nowrap"
            >
              Request a Consultation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
